import type { AxiosError, AxiosInstance } from "axios";
import axios from "axios";

import { format } from "date-fns";
import { ACCESS_TOKEN_KEY } from "../constants";
import { reportApiError } from "../sentry";
import {
  AuthToken,
  Autobooking,
  Booking,
  BookingRenameRequest,
  BookingRequest,
  BookingSummary,
  BookingUpdateRequest,
  CopyBookingsRequest,
  Court,
  CreateAutobookingRequest,
  CreateCourtRequest,
  CreateServiceHourRequest,
  Hour,
  HttpError,
  HttpMethods,
  PayBookingsRequest,
  PayBookingsResponse,
  Price,
  PriceUpdateRequest,
  Receipt,
  ReceiptPreview,
  RequestResetPasswordRequest,
  ResetPasswordRequest,
  ServiceHour,
  Settings,
  SettingsUpdateRequest,
  SignupRequest,
  SignupResponse,
  StatisticsResponse,
  UpdateAutobookingRequest,
  UpdateCourtRequest,
  UpdateServiceHourRequest,
  UpdateUserRequest,
  User,
  UserReadModel,
  UserBookingSummaryItem,
  UnpaidBookingItem,
  ValidateResetCodeResponse,
  ManagementDeleteUserRequest,
  ManagementUpdateUserRequest,
} from "./types";

/**
 * Api is used to handle all interactions with the backend of this service
 */
class Api {
  instance: AxiosInstance;
  error: HttpError | undefined;
  baseUrl: string;

  constructor(baseURL: string) {
    this.instance = axios.create({
      baseURL,
      timeout: 10000,
      headers: { "Content-Type": "application/json" },
    });
    this.baseUrl = baseURL;
    this.error = undefined;
  }

  private async makeApiCall<T>(
    method: HttpMethods,
    url: string,
    payload?: Record<string, unknown>,
    params?: Record<string, unknown>,
    authorized = true,
    responseType: "json" | "blob" = "json",
  ): Promise<T | undefined> {
    let headers: Record<string, string> = {};
    if (authorized) {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (accessToken) {
        headers = { Authorization: `Bearer ${accessToken}` };
      }
    }

    try {
      this.error = undefined;
      const { data } = await this.instance({
        method,
        url,
        data: payload,
        params,
        headers,
        responseType,
      });

      return data;
    } catch (e) {
      console.log(e);
      const error = e as Error | AxiosError;
      if (axios.isAxiosError(error) && error.response) {
        this.error = { code: error.response.status, data: error.response.data };
        if (error.response.status >= 500) {
          reportApiError(method, url, error.response.status, error.message);
        }
      } else {
        this.error = { code: 500, data: error.message };
      }

      // handle token expiration
      if (this.error.code === 401 && url !== "/auth/login") {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        window.location.reload();
      }

      return undefined;
    }
  }

  /**
   * Perform login
   * @param {string} username user username
   * @param {string} password user password
   * @returns {AuthToken} the user authentication token
   */
  async login(username: string, password: string): Promise<AuthToken | undefined> {
    const payload = {
      username,
      password,
    };

    return this.makeApiCall<AuthToken>(HttpMethods.POST, "/auth/login", payload, undefined, false);
  }

  /**
   * Get current logged in user
   * @returns {User} the user authenticated
   */
  async getCurrentUser(): Promise<User | undefined> {
    return this.makeApiCall<User>(HttpMethods.GET, "/users/me");
  }

  /**
   * Signup user
   * @param {SignupRequest} request signup payload
   * @returns {SignupResponse} the response from the signup procedure
   */
  async signup(request: SignupRequest): Promise<SignupResponse | undefined> {
    const payload = { ...request };

    return this.makeApiCall<SignupResponse>(HttpMethods.POST, "/auth/signup", payload);
  }

  /**
   * Activate user
   * @param {SignupActivationRequest} request signup activation payload
   */
  async activate(username: string, authCode: string): Promise<undefined> {
    const payload = {
      username,
      auth_code: authCode,
    };

    return this.makeApiCall<undefined>(HttpMethods.POST, "/auth/signup/activate", payload);
  }

  /**
   * Send activation link
   * @param {SignupActivationRequest} request signup activation payload
   */
  async sendActivationLink(userId: number): Promise<undefined> {
    const payload = { user_id: userId };

    return this.makeApiCall<undefined>(
      HttpMethods.POST,
      "/auth/signup/activationlink/send",
      payload,
    );
  }

  /**
   * Request reset password
   * @param {string} email the email of the user you are requesting the reset password
   */
  async requestResetPassword(email: string): Promise<undefined> {
    const payload: RequestResetPasswordRequest = { email };

    return this.makeApiCall<undefined>(HttpMethods.POST, "/auth/resetpassword/request", payload);
  }

  /**
   * Check if a reset code is valid for the user
   * @param {string} username the user you are requesting the reset password
   * @param {string} code the reset code to validate
   */
  async validateResetCode(
    username: string,
    code: string,
  ): Promise<ValidateResetCodeResponse | undefined> {
    const payload = { username, reset_code: code };

    return this.makeApiCall<ValidateResetCodeResponse | undefined>(
      HttpMethods.POST,
      "/auth/resetpassword/validate",
      payload,
    );
  }

  /**
   * Reset password for a user
   * @param {string} email the email of the user you are requesting the reset password
   */
  async resetPassword(request: ResetPasswordRequest): Promise<undefined> {
    const payload = { ...request };

    return this.makeApiCall<undefined>(HttpMethods.POST, "/auth/resetpassword", payload);
  }

  /**
   * Search users by username
   * @param {string} username partial username to search for
   * @returns {User[]} list of matching users
   */
  async searchUsers(username: string): Promise<User[]> {
    const result = await this.makeApiCall<User[]>(
      HttpMethods.GET,
      `/users/search?username=${username}`,
    );
    return result || [];
  }

  /**
   * Update user
   * @param {UpdateUserRequest} request update user payload
   */
  async updateUser(request: UpdateUserRequest): Promise<User | undefined> {
    const payload = { ...request };

    return this.makeApiCall<undefined>(HttpMethods.PATCH, "/users", payload);
  }

  /**
   * Get management users list
   * @returns {UserReadModel[]} list of users for management
   */
  async getManagementUsers(): Promise<UserReadModel[] | undefined> {
    return this.makeApiCall<UserReadModel[]>(HttpMethods.GET, "/users/management/list");
  }

  /**
   * Bulk update users
   * @param {ManagementUpdateUserRequest} request bulk update request
   */
  async bulkUpdateUsers(request: ManagementUpdateUserRequest): Promise<undefined> {
    const payload = { ...request };
    return this.makeApiCall<undefined>(HttpMethods.POST, "/users/management/update", payload);
  }

  /**
   * Bulk delete users
   * @param {ManagementDeleteUserRequest} request bulk delete request
   */
  async bulkDeleteUsers(request: ManagementDeleteUserRequest): Promise<undefined> {
    const payload = { ...request };
    return this.makeApiCall<undefined>(HttpMethods.POST, "/users/management/delete", payload);
  }

  /**
   * Open users list as PDF in a new tab
   * @param {boolean} compact Generate compact PDF with only name, surname, username and is_member columns
   */
  async printUsersList(compact = false): Promise<void> {
    const content = await this.makeApiCall<undefined>(
      HttpMethods.GET,
      "/users/management/list/print",
      undefined,
      { compact },
      true,
      "blob",
    );
    if (content) {
      const blob = new Blob([content], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  }

  /**
   * Get Bookings
   * @param {Date} date_from date from filter
   * @param {Date} date_to date to filter
   * @returns {Booking[]} list of bookings
   */
  async getBookings(dateFrom: Date, dateTo: Date, courtId: number): Promise<Booking[] | undefined> {
    const params = {
      date_from: format(dateFrom, "yyyy-MM-dd"),
      date_to: format(dateTo, "yyyy-MM-dd"),
      court_id: courtId,
    };

    return this.makeApiCall<Booking[]>(HttpMethods.GET, "/bookings", undefined, params);
  }

  /**
   * Request a new booking
   * @param {BookingRequest} request booking request payload
   */
  async requestBooking(request: BookingRequest): Promise<Booking | undefined> {
    const payload = { ...request, date: format(request.date, "yyyy-MM-dd") };

    return this.makeApiCall<Booking>(HttpMethods.PUT, "/bookings", payload);
  }

  /**
   * Update an existing booking
   * @param {BookingUpdateRequest} request booking update request payload
   */
  async updateBooking(
    bookingId: number,
    request: BookingUpdateRequest,
  ): Promise<Booking | undefined> {
    const payload = { ...request };

    return this.makeApiCall<Booking>(HttpMethods.PATCH, `/bookings/${bookingId}`, payload);
  }

  /**
   * Cancel a booking
   * @param {number} bookingId identifier of the booking
   */
  async cancelBooking(bookingId: number): Promise<undefined> {
    return this.makeApiCall<undefined>(HttpMethods.POST, `/bookings/${bookingId}/cancel`);
  }

  /**
   * Rename a booking
   * @param {number} bookingId identifier of the booking
   * @param {BookingRenameRequest} request booking rename request payload
   */
  async renameBooking(
    bookingId: number,
    request: BookingRenameRequest,
  ): Promise<Booking | undefined> {
    const payload = { ...request };

    return this.makeApiCall<Booking>(HttpMethods.POST, `/bookings/${bookingId}/rename`, payload);
  }

  /**
   * Pay a list of bookings
   * @param {PayBookingsRequest} request paylod of the pay bookings request
   */
  async payBookings(request: PayBookingsRequest): Promise<PayBookingsResponse | undefined> {
    const payload = { ...request };

    return this.makeApiCall<undefined>(HttpMethods.POST, "/bookings/pay", payload);
  }

  /**
   * Get booking summary for management
   * @param {Date} dateFrom date from filter
   * @param {Date} dateTo date to filter
   * @returns {BookingSummary} booking summary with monthly statistics
   */
  async getBookingSummary(dateFrom: Date, dateTo: Date): Promise<BookingSummary | undefined> {
    const params = {
      date_from: format(dateFrom, "yyyy-MM-dd"),
      date_to: format(dateTo, "yyyy-MM-dd"),
    };

    return this.makeApiCall<BookingSummary>(
      HttpMethods.GET,
      "/bookings/management/summary",
      undefined,
      params,
    );
  }

  /**
   * Open booking summary as PDF in a new tab
   * @param {Date} dateFrom Start date for the summary
   * @param {Date} dateTo End date for the summary
   */
  async printBookingSummary(dateFrom: Date, dateTo: Date): Promise<void> {
    const params = {
      date_from: format(dateFrom, "yyyy-MM-dd"),
      date_to: format(dateTo, "yyyy-MM-dd"),
    };
    const content = await this.makeApiCall<undefined>(
      HttpMethods.GET,
      "/bookings/management/summary/print",
      undefined,
      params,
      true,
      "blob",
    );
    if (content) {
      const blob = new Blob([content], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  }

  /**
   * Get user booking summary
   * @param {Date} dateFrom Start date for the summary
   * @param {Date} dateTo End date for the summary
   * @returns {UserBookingSummaryItem[]} list of user booking statistics
   */
  async getUserBookingSummary(
    dateFrom: Date,
    dateTo: Date,
  ): Promise<UserBookingSummaryItem[] | undefined> {
    const params = {
      date_from: format(dateFrom, "yyyy-MM-dd"),
      date_to: format(dateTo, "yyyy-MM-dd"),
    };

    return this.makeApiCall<UserBookingSummaryItem[]>(
      HttpMethods.GET,
      "/bookings/management/users/summary",
      undefined,
      params,
    );
  }

  /**
   * Open user booking summary as PDF in a new tab
   * @param {Date} dateFrom Start date for the summary
   * @param {Date} dateTo End date for the summary
   */
  async printUserBookingSummary(dateFrom: Date, dateTo: Date): Promise<void> {
    const params = {
      date_from: format(dateFrom, "yyyy-MM-dd"),
      date_to: format(dateTo, "yyyy-MM-dd"),
    };
    const content = await this.makeApiCall<undefined>(
      HttpMethods.GET,
      "/bookings/management/users/summary/print",
      undefined,
      params,
      true,
      "blob",
    );
    if (content) {
      const blob = new Blob([content], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  }

  /**
   * Get unpaid bookings for management
   * @param {Date} dateFrom Start date for the unpaid bookings
   * @param {Date} dateTo End date for the unpaid bookings
   * @param {string | null} username Optional username filter
   * @returns {UnpaidBookingItem[]} list of unpaid bookings
   */
  async getUnpaidBookings(
    dateFrom: Date,
    dateTo: Date,
    username: string | null = null,
  ): Promise<UnpaidBookingItem[] | undefined> {
    const params: Record<string, unknown> = {
      date_from: format(dateFrom, "yyyy-MM-dd"),
      date_to: format(dateTo, "yyyy-MM-dd"),
    };
    if (username) {
      params.username = username;
    }

    return this.makeApiCall<UnpaidBookingItem[]>(
      HttpMethods.GET,
      "/bookings/management/unpaid",
      undefined,
      params,
    );
  }

  /**
   * Open unpaid bookings list as PDF in a new tab
   * @param {Date} dateFrom Start date for the unpaid bookings
   * @param {Date} dateTo End date for the unpaid bookings
   * @param {string | null} username Optional username filter
   */
  async printUnpaidBookings(
    dateFrom: Date,
    dateTo: Date,
    username: string | null = null,
  ): Promise<void> {
    const params: Record<string, unknown> = {
      date_from: format(dateFrom, "yyyy-MM-dd"),
      date_to: format(dateTo, "yyyy-MM-dd"),
    };
    if (username) {
      params.username = username;
    }
    const content = await this.makeApiCall<undefined>(
      HttpMethods.GET,
      "/bookings/management/unpaid/print",
      undefined,
      params,
      true,
      "blob",
    );
    if (content) {
      const blob = new Blob([content], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  }

  async copyWeek(request: CopyBookingsRequest): Promise<undefined> {
    const payload = {
      source_date: format(request.source_date, "yyyy-MM-dd"),
      source_court_id: request.source_court_id,
      target_date: format(request.target_date, "yyyy-MM-dd"),
      target_court_id: request.target_court_id,
    };

    return this.makeApiCall<undefined>(HttpMethods.POST, "/bookings/copy", payload);
  }

  /**
   * Get receipt
   * @param {number} receiptId identifier of the receipt
   * @returns {Receipt} the requested receipt
   */
  async getReceipt(receiptId: number): Promise<Receipt | undefined> {
    return this.makeApiCall<Receipt>(HttpMethods.GET, `/receipts/${receiptId}`);
  }

  /**
   * Get receipt preview from bookings
   * @returns {ReceiptPreview} preview of the receipt with number, name, cost and hours
   */
  async getReceiptPreview(bookingIds: number[]): Promise<ReceiptPreview | undefined> {
    const params = bookingIds.map(id => `booking_ids=${id}`).join("&");
    return this.makeApiCall<ReceiptPreview>(HttpMethods.GET, `/receipts/preview?${params}`);
  }

  /**
   * Get management receipts list
   * @param {Date} dateFrom date from filter
   * @param {Date} dateTo date to filter
   * @returns {Receipt[]} list of receipts
   */
  async getManagementReceipts(dateFrom: Date, dateTo: Date): Promise<Receipt[] | undefined> {
    const params = {
      date_from: format(dateFrom, "yyyy-MM-dd"),
      date_to: format(dateTo, "yyyy-MM-dd"),
    };

    return this.makeApiCall<Receipt[]>(
      HttpMethods.GET,
      "/receipts/management/list",
      undefined,
      params,
    );
  }

  /**
   * Open receipt list as PDF in a new tab
   * @param {Date} dateFrom Start date for the list
   * @param {Date} dateTo End date for the list
   */
  async printReceiptList(dateFrom: Date, dateTo: Date): Promise<void> {
    const params = {
      date_from: format(dateFrom, "yyyy-MM-dd"),
      date_to: format(dateTo, "yyyy-MM-dd"),
    };
    const content = await this.makeApiCall<undefined>(
      HttpMethods.GET,
      "/receipts/management/list/print",
      undefined,
      params,
      true,
      "blob",
    );
    if (content) {
      const blob = new Blob([content], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  }

  /**
   * Get courts
   * @returns {Court[]} list of courts
   */
  async getCourts(): Promise<Court[] | undefined> {
    return this.makeApiCall<Court[]>(HttpMethods.GET, "/courts");
  }

  /**
   * create a court
   * @param {CreateCourtRequest} schema create court request payload
   * @returns {Court} created court
   */
  async createCourt(schema: CreateCourtRequest): Promise<Court | undefined> {
    const payload = { ...schema };

    return this.makeApiCall<Court>(HttpMethods.PUT, "/courts", payload);
  }

  /**
   * Update a court
   * @param {number} courtId identifier of the court to update
   * @param {UpdateCourtRequest} schema update court request payload
   * @returns {Court} updated court
   */
  async updateCourt(courtId: number, schema: UpdateCourtRequest): Promise<Court | undefined> {
    const payload = { ...schema };

    return this.makeApiCall<Court>(HttpMethods.PATCH, `/courts/${courtId}`, payload);
  }

  /**
   * Delete a court
   * @param {number} courtId identifier of the court to update
   * @returns {Court} deleted court
   */
  async deleteCourt(courtId: number): Promise<Court | undefined> {
    return this.makeApiCall<Court>(HttpMethods.DELETE, `/courts/${courtId}`);
  }

  /**
   * Get hours
   * @returns {Hour[]} list of hours
   */
  async getHours(): Promise<Hour[] | undefined> {
    return this.makeApiCall<Hour[]>(HttpMethods.GET, "/hours");
  }

  /**
   * Get prices
   * @returns {Price[]} list of prices
   */
  async getPrices(): Promise<Price[] | undefined> {
    return this.makeApiCall<Price[]>(HttpMethods.GET, "/prices");
  }

  /**
   * Update prices
   * @param {PriceUpdateRequest} request price update request payload
   * @returns {Price[]} updated list of prices
   */
  async updatePrices(request: PriceUpdateRequest): Promise<Price[] | undefined> {
    const payload = { ...request };

    return this.makeApiCall<Price[]>(HttpMethods.PATCH, "/prices", payload);
  }

  /**
   * Get settings
   * @returns {Settings} settings object
   */
  async getSettings(): Promise<Settings | undefined> {
    return this.makeApiCall<Settings>(HttpMethods.GET, "/settings");
  }

  /**
   * Update settings
   * @returns {Settings} settings object
   */
  async updateSettings(request: SettingsUpdateRequest): Promise<Settings | undefined> {
    const payload = { ...request };

    return this.makeApiCall<Settings>(HttpMethods.PATCH, "/settings", payload);
  }

  /**
   * Get service hours
   * @returns {ServiceHour[]} list of service hours
   */
  async getServiceHours(): Promise<ServiceHour[] | undefined> {
    return this.makeApiCall<ServiceHour[]>(HttpMethods.GET, "/service-hours");
  }

  /**
   * Create a service hour
   * @param {CreateServiceHourRequest} request service hour request payload
   * @returns {ServiceHour} created service hour
   */
  async createServiceHour(request: CreateServiceHourRequest): Promise<ServiceHour | undefined> {
    const payload = { ...request };

    return this.makeApiCall<ServiceHour>(HttpMethods.POST, "/service-hours", payload);
  }

  /**
   * Update a service hour
   * @param {number} serviceHourId identifier of the service hour to update
   * @param {UpdateServiceHourRequest} request service hour update request payload
   * @returns {ServiceHour} updated service hour
   */
  async updateServiceHour(
    serviceHourId: number,
    request: UpdateServiceHourRequest,
  ): Promise<ServiceHour | undefined> {
    const payload = { ...request };

    return this.makeApiCall<ServiceHour>(
      HttpMethods.PATCH,
      `/service-hours/${serviceHourId}`,
      payload,
    );
  }

  /**
   * Delete a service hour
   * @param {number} serviceHourId identifier of the service hour to delete
   * @returns {ServiceHour} deleted service hour
   */
  async deleteServiceHour(serviceHourId: number): Promise<ServiceHour | undefined> {
    return this.makeApiCall<ServiceHour>(HttpMethods.DELETE, `/service-hours/${serviceHourId}`);
  }

  /**
   * Get autobookings
   * @returns {Autobooking[]} list of autobookings
   */
  async getAutobookings(): Promise<Autobooking[] | undefined> {
    return this.makeApiCall<Autobooking[]>(HttpMethods.GET, "/autobookings");
  }

  /**
   * Create an autobooking
   * @param {CreateAutobookingRequest} request autobooking request payload
   * @returns {Autobooking} created autobooking
   */
  async createAutobooking(request: CreateAutobookingRequest): Promise<Autobooking | undefined> {
    const payload = { ...request };

    return this.makeApiCall<Autobooking>(HttpMethods.POST, "/autobookings", payload);
  }

  /**
   * Update an autobooking
   * @param {number} autobookingId identifier of the autobooking to update
   * @param {UpdateAutobookingRequest} request autobooking update request payload
   * @returns {Autobooking} updated autobooking
   */
  async updateAutobooking(
    autobookingId: number,
    request: UpdateAutobookingRequest,
  ): Promise<Autobooking | undefined> {
    const payload = { ...request };

    return this.makeApiCall<Autobooking>(
      HttpMethods.PATCH,
      `/autobookings/${autobookingId}`,
      payload,
    );
  }

  /**
   * Delete an autobooking
   * @param {number} autobookingId identifier of the autobooking to delete
   */
  async deleteAutobooking(autobookingId: number): Promise<undefined> {
    return this.makeApiCall<undefined>(HttpMethods.DELETE, `/autobookings/${autobookingId}`);
  }

  /**
   * Get statistics for a given year
   * @param {number} year the year to get statistics for
   * @returns {StatisticsResponse} statistics data for the year
   */
  async getStatistics(year: number): Promise<StatisticsResponse | undefined> {
    const params = {
      year,
    };

    return this.makeApiCall<StatisticsResponse>(HttpMethods.GET, "/stats", undefined, params);
  }
}

export default Api;
